// src/generators/dzial1_liczby_i_dzialania/temat5_teoria_liczb/podtemat3_nwd_nww_types/NwdNwwUnifiedTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ===== Utils: gcd/lcm ===== */
function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b !== 0) { const r = a % b; a = b; b = r; }
  return a;
}
function lcm(a, b) {
  if (a === 0 || b === 0) return 0;
  return Math.abs((a / gcd(a, b)) * b);
}
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const sample = (arr) => arr[randInt(0, arr.length - 1)];
const areCoprime = (x, y) => gcd(x, y) === 1;

/* ===== Faktoryzacja i profil czynników – takie same zasady jak w PrimeFactorizationBasicTask ===== */

// rozkład na czynniki pierwsze -> Map(prime -> exponent)
function primeFactorMap(n) {
  let x = Math.abs(n);
  const m = new Map();
  let p = 2;
  while (p * p <= x) {
    while (x % p === 0) {
      m.set(p, (m.get(p) || 0) + 1);
      x = Math.floor(x / p);
    }
    p = p === 2 ? 3 : p + 2; // 2,3,5,7,...
  }
  if (x > 1) m.set(x, (m.get(x) || 0) + 1);
  return m;
}

// Map nie przechodzi przez JSON wprost. Starsze snapshoty mają w tych polach
// pusty obiekt, a nowsze mogą zawierać zwykły obiekt albo tablicę wpisów.
// Odtworzenie rozkładu z liczby pozwala poprawnie otwierać oba warianty.
function restoreFactorMap(value, fallbackNumber) {
  if (value instanceof Map && value.size > 0) return value;

  const entries = Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? Object.entries(value)
      : [];
  const restored = new Map(
    entries
      .map(([prime, exponent]) => [Number(prime), Number(exponent)])
      .filter(([prime, exponent]) => Number.isFinite(prime) && Number.isFinite(exponent) && exponent > 0)
  );
  return restored.size > 0 ? restored : primeFactorMap(fallbackNumber);
}

// z mapy na tablicę [p,p,...]
function expandFactorMap(m) {
  const arr = [];
  for (const [p, e] of m.entries()) {
    for (let i = 0; i < e; i++) arr.push(p);
  }
  return arr.sort((a, b) => a - b);
}

// wersja do potęg (może się jeszcze przydać gdzie indziej)
function factorMapToLatex(m) {
  const parts = [...m.entries()]
    .sort((a,b)=>a[0]-b[0])
    .map(([p, e]) => (e === 1 ? `${p}` : `${p}^{${e}}`));
  return parts.length ? parts.join("\\cdot") : "1";
}

// NOWE: iloczyn „rozpisany”, bez potęg
function factorMapToExpandedLatex(m) {
  const factors = expandFactorMap(m);
  return factors.length ? factors.join("\\cdot") : "1";
}

function allFactorsBelow50(factors) {
  return factors.every((p) => p < 50);
}

/**
 * DOKŁADNIE te same zasady, co w PrimeFactorizationBasicTask:
 * - 2,3,5: krotność ≤ 4
 * - 7,11,13: krotność ≤ 2
 * - 17,19,...,47: krotność ≤ 1
 * - wszystkie czynniki < 50
 * - w jednym rozkładzie maksymalnie jedna „trudniejsza” liczba pierwsza (>5)
 */
function factorsProfileOk(factors) {
  if (!allFactorsBelow50(factors)) return false;

  const counts = new Map();
  for (const p of factors) {
    counts.set(p, (counts.get(p) || 0) + 1);
  }

  const hardPrimes = [];

  for (const [p, c] of counts.entries()) {
    if (p === 2 || p === 3 || p === 5) {
      if (c > 4) return false;
    } else if (p === 7 || p === 11 || p === 13) {
      if (c > 2) return false;
      hardPrimes.push(p);
    } else {
      // większe: 17–47
      if (c > 1) return false;
      hardPrimes.push(p);
    }
  }

  if (hardPrimes.length > 1) return false;
  return true;
}

function numberProfileOk(n) {
  const m = primeFactorMap(n);
  const flat = expandFactorMap(m);
  return factorsProfileOk(flat);
}

/* --- zapis słupkowy --- */
function ladderLatex(n0, factors) {
  let n = n0;
  const rows = factors.map((p) => {
    const row = `${n} & ${p} \\\\`;
    n = n / p;
    return row;
  });
  rows.push(`1 & \\\\`);
  return `\\begin{array}{r|l}
${rows.join("\n")}
\\end{array}`;
}

function combineMin(aMap, bMap) {
  const out = new Map();
  for (const [p, ea] of aMap) {
    if (bMap.has(p)) out.set(p, Math.min(ea, bMap.get(p)));
  }
  for (const [p, e] of [...out]) if (e <= 0) out.delete(p);
  return out;
}
function combineMax(aMap, bMap) {
  const out = new Map(aMap);
  for (const [p, eb] of bMap) {
    out.set(p, Math.max(eb, out.get(p) || 0));
  }
  return out;
}
function valueFromFactorMap(m) {
  let v = 1;
  for (const [p, e] of m) v *= p ** e;
  return v;
}

// „nadmiarowe” czynniki w aMap względem commonMap
function diffFactorMap(aMap, commonMap) {
  const out = new Map();
  for (const [p, ea] of aMap) {
    const ec = commonMap.get(p) || 0;
    if (ea > ec) out.set(p, ea - ec);
  }
  return out;
}

/* ===== Losowanie pary liczb – z filtrem „ładnych” czynników ===== */

function randCoprimePair(aMin, aMax, bMin, bMax, maxTries = 200) {
  for (let i = 0; i < maxTries; i++) {
    const a = randInt(aMin, aMax);
    const b = randInt(bMin, bMax);
    if (areCoprime(a, b)) return [a, b];
  }
  return [randInt(aMin, aMax), randInt(bMin, bMax)];
}

function pickG() {
  const composite = [4, 6, 8, 9, 10, 12];
  const primes = [2, 3, 5, 7, 11];
  const bucket = sample(["C","C","C","C","C","P","P","P","ONE","ONE","G1"]);
  if (bucket === "C") return sample(composite);
  if (bucket === "P") return sample(primes);
  if (bucket === "G1") return 1;
  return sample([...composite, ...primes]);
}

function safeRandomInRange(range) {
  while (true) {
    const n = randInt(range[0], range[1]);
    if (numberProfileOk(n)) return n;
  }
}

function makePairByG(g, xRange, yRange, preferClose=false) {
  const aMin = 2, aMax = 50, bMin = 2, bMax = 50;

  for (let tries = 0; tries < 500; tries++) {
    let [a, b] = randCoprimePair(aMin, aMax, bMin, bMax);
    let x = g * a, y = g * b;

    if (preferClose && Math.abs(x - y) > 25) continue;
    if (x < xRange[0] || x > xRange[1]) continue;
    if (y < yRange[0] || y > yRange[1]) continue;

    // czasem „jedna dzieli drugą”
    if (Math.random() < 0.08) {
      if (Math.random() < 0.5) y = x * randInt(2, 3);
      else x = y * randInt(2, 3);
      if (x < xRange[0] || x > xRange[1] || y < yRange[0] || y > yRange[1]) {
        continue;
      }
    }

    if (!numberProfileOk(x) || !numberProfileOk(y)) continue;

    return { x, y };
  }

  return {
    x: safeRandomInRange(xRange),
    y: safeRandomInRange(yRange),
  };
}

/* ===== Generator (zwraca {type,data}) ===== */
export function generateTask() {
  return generateTaskWith({ mode: "BOTH", difficulty: "EASY" });
}

// mode: "NWD" | "NWW" | "BOTH"; difficulty: "EASY" | "HARD"
export function generateTaskWith({ mode = "BOTH", difficulty = "EASY" } = {}) {
  const easyX = [12, 99],  easyY = [100, 999];
  const hardX = [100, 999], hardY = [1000, 9999];

  const xRange = difficulty === "HARD" ? hardX : easyX;
  const yRange = difficulty === "HARD" ? hardY : easyY;

  const g = pickG();
  const preferClose = Math.random() < 0.2;
  const { x, y } = makePairByG(g, xRange, yRange, preferClose);

  const factA = primeFactorMap(x);
  const factB = primeFactorMap(y);
  const factG = combineMin(factA, factB);
  const factL = combineMax(factA, factB);

  const G = valueFromFactorMap(factG);
  const L = valueFromFactorMap(factL);

  return {
    type: "NwdNwwUnifiedTask",
    data: {
      mode, difficulty,
      x, y,
      gcd: G, lcm: L,
      factA, factB, factG, factL
    }
  };
}

/* ===== Renderery ===== */
export function renderTask({ data }) {
  const { x, y, mode } = data;
  const pairLatex = `(${x}\\,;\\,${y})`;

  return (
    <div style={{ marginBottom: 18 }}>
      <div>
        Dane: <InlineMath math={pairLatex} />
      </div>

      {mode === "NWD" && (
        <div>
          Oblicz <InlineMath math={'\\mathrm{NWD}'} />{" "}
          <InlineMath math={pairLatex} />.
        </div>
      )}

      {mode === "NWW" && (
        <div>
          Oblicz <InlineMath math={'\\mathrm{NWW}'} />{" "}
          <InlineMath math={pairLatex} />.
        </div>
      )}

      {mode === "BOTH" && (
        <div>
          Oblicz <InlineMath math={'\\mathrm{NWD}'} /> oraz{" "}
          <InlineMath math={'\\mathrm{NWW}'} />{" "}
          <InlineMath math={pairLatex} />.
        </div>
      )}
    </div>
  );
}

/* ===== Input ===== */

function extractNumeric(val) {
  if (val && typeof val === "object") {
    const p = val.parsed;
    if (typeof p === "number") return p;
    if (
      typeof val.raw === "string" &&
      val.raw.trim() !== "" &&
      !Number.isNaN(Number(val.raw))
    ) {
      return Number(val.raw);
    }
    return NaN;
  }
  if (typeof val === "number") return val;
  if (typeof val === "string") return Number(val);
  return NaN;
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const needNwd = data.mode !== "NWW";
  const needNww = data.mode !== "NWD";
  const ua = userAnswer ?? {};
  const set = (k, v) => onChange?.({ ...(ua || {}), [k]: v });

  return (
    <div className="max-w-sm">
      {needNwd && (
        <div style={{ marginBottom: needNww ? 18 : 0 }}>
          <div className="text-sm text-neutral-400" style={{ marginBottom: 6 }}>
            <InlineMath math={'\\mathrm{NWD}'} />:
          </div>
          <FractionInput
            value={ua.nwd ?? ""}
            onChange={(v) => set("nwd", v)}
            disabled={disabled}
            placeholder="np. 12"
            label="\mathrm{NWD}"
          />
        </div>
      )}

      {needNww && (
        <div style={{ marginTop: needNwd ? 8 : 0 }}>
          <div className="text-sm text-neutral-400" style={{ marginBottom: 6 }}>
            <InlineMath math={'\\mathrm{NWW}'} />:
          </div>
          <FractionInput
            value={ua.nww ?? ""}
            onChange={(v) => set("nww", v)}
            disabled={disabled}
            placeholder="np. 180"
            label="\mathrm{NWW}"
          />
        </div>
      )}
    </div>
  );
}


/* ===== Sprawdzanie ===== */
/* ===== Sprawdzanie ===== */
export function checkAnswer({ data }, userAnswer) {
  const needNwd = data.mode !== "NWW";
  const needNww = data.mode !== "NWD";

  const uNwd = extractNumeric(userAnswer?.nwd);
  const uNww = extractNumeric(userAnswer?.nww);

  const okNwd = needNwd && Number.isFinite(uNwd) && uNwd === data.gcd;
  const okNww = needNww && Number.isFinite(uNww) && uNww === data.lcm;

  // Tryb z dwoma rzeczami naraz: zadanie na 2 punkty
  if (data.mode === "BOTH") {
    const score = (okNwd ? 1 : 0) + (okNww ? 1 : 0);
    const parts = {
      NWD: okNwd,
      NWW: okNww,
    };
    return {
      isCorrect: score === 2,
      score,
      maxScore: 2,
      parts,
    };
  }

  // Pozostałe tryby (tylko NWD lub tylko NWW) – klasyczne 0/1
  // needNwd / needNww mówią, którą część w ogóle bierzemy pod uwagę
  const singleOk = (needNwd ? okNwd : true) && (needNww ? okNww : true);
  return singleOk;
}


/* ===== Odpowiedź poprawna ===== */
export function renderAnswer({ data }) {
  const { mode, x, y, gcd: G, lcm: L } = data;
  const pairLatex = `(${x}\\,;\\,${y})`;

  return (
    <div className="text-sm space-y-1">
      {mode !== "NWW" && (
        <div>
          <InlineMath math={`\\mathrm{NWD}${pairLatex} = ${G}`} />
        </div>
      )}
      {mode !== "NWD" && (
        <div>
          <InlineMath math={`\\mathrm{NWW}${pairLatex} = ${L}`} />
        </div>
      )}
    </div>
  );
}

/* ===== Odpowiedź ucznia ===== */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || (userAnswer.nwd == null && userAnswer.nww == null)) {
    return <i>(brak odpowiedzi)</i>;
  }

  const uNwd = extractNumeric(userAnswer?.nwd);
  const uNww = extractNumeric(userAnswer?.nww);

  const parts = [];
  if (!Number.isNaN(uNwd)) {
    parts.push(`\\mathrm{NWD} = ${uNwd}`);
  }
  if (!Number.isNaN(uNww)) {
    parts.push(`\\mathrm{NWW} = ${uNww}`);
  }

  if (parts.length === 0) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={parts.join("\\quad ")} />;
}

/* ===== Wyjaśnienie ===== */
export function renderExplanation({ data }) {
  const { mode, x, y, gcd: G, lcm: L } = data;
  const factA = restoreFactorMap(data.factA, x);
  const factB = restoreFactorMap(data.factB, y);
  const factG = restoreFactorMap(data.factG, G);

  // rozkłady zapisane BEZ potęg (2·2·5 zamiast 2^2·5)
  const latexA = factorMapToExpandedLatex(factA);
  const latexB = factorMapToExpandedLatex(factB);
  const latexG = factorMapToExpandedLatex(factG);

  const factorsA = expandFactorMap(factA);
  const factorsB = expandFactorMap(factB);

  const ladderA = ladderLatex(x, factorsA);
  const ladderB = ladderLatex(y, factorsB);

  // „nadmiarowe” czynniki w każdej liczbie względem NWD
  const extraA = diffFactorMap(factA, factG); // tylko w x (pierwszej liczbie)
  const extraB = diffFactorMap(factB, factG); // tylko w y (drugiej liczbie)

  const extraAFactors = expandFactorMap(extraA);
  const extraBFactors = expandFactorMap(extraB);

  const latexExtraA = extraAFactors.length ? extraAFactors.join("\\cdot") : "";
  const latexExtraB = extraBFactors.length ? extraBFactors.join("\\cdot") : "";

  // wybierz „główną” metodę dla NWW – tę z mniejszą liczbą dodatkowych czynników
  const lenA = extraAFactors.length;
  const lenB = extraBFactors.length;
  const useAFirst = lenA > 0 && (lenA <= lenB || lenB === 0);

  // zapis NWD: unikamy 5 = 5 przy pojedynczym czynniku
  const nwdFormula =
    latexG === String(G)
      ? `\\mathrm{NWD}(${x}\\,;\\,${y}) = ${G}`
      : `\\mathrm{NWD}(${x}\\,;\\,${y}) = ${latexG} = ${G}`;

  return (
    <div style={{ display: "grid", rowGap: 10 }}>
      <p>
        <b>Krok 1:</b> Rozłóż obie liczby na czynniki pierwsze – możesz to zrobić
        zapisem słupkowym, dzieląc kolejno przez liczby pierwsze.
      </p>

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div>
          <BlockMath math={ladderA} />
          <InlineMath math={`${x} = ${latexA}`} />
        </div>
        <div>
          <BlockMath math={ladderB} />
          <InlineMath math={`${y} = ${latexB}`} />
        </div>
      </div>

      <p>
        <b>Krok 2:</b> Na podstawie rozkładów odczytaj{" "}
        <InlineMath math={'\\mathrm{NWD}'} /> oraz{" "}
        <InlineMath math={'\\mathrm{NWW}'} />.
      </p>

      {(mode === "NWD" || mode === "BOTH") && (
        <div>
          <div className="text-sm">
            Do <b>NWD</b> bierzemy tylko te czynniki pierwsze, które
            <b> powtarzają się w rozkładzie obu liczb</b>. Jeżeli jakiś czynnik
            występuje kilka razy w obu liczbach, to w NWD zapisujemy go tyle razy,
            ile razy występuje jednocześnie w obu rozkładach.
          </div>
          <InlineMath math={nwdFormula} />
        </div>
      )}

      {(mode === "NWW" || mode === "BOTH") && (
        <div>
          <div className="text-sm" style={{ marginBottom: 4 }}>
            Aby obliczyć <b>NWW</b>, bierzemy <b>całą jedną z liczb</b> i
            dopisujemy do niej te czynniki pierwsze drugiej liczby, które w niej
            się nie pojawiają.
          </div>

          {/* główna metoda */}
          {useAFirst && latexExtraA && (
            <BlockMath
              math={`\\mathrm{NWW}(${x}\\,;\\,${y}) = ${y} \\cdot ${latexExtraA} = ${L}`}
            />
          )}
          {!useAFirst && latexExtraB && (
            <BlockMath
              math={`\\mathrm{NWW}(${x}\\,;\\,${y}) = ${x} \\cdot ${latexExtraB} = ${L}`}
            />
          )}

          {/* sprawdzenie – start od drugiej liczby */}
          <div className="text-sm" style={{ marginTop: 6 }}>
            Dla sprawdzenia możemy zacząć od drugiej liczby – wynik powinien
            wyjść taki sam.
          </div>
          {useAFirst && latexExtraB && (
            <BlockMath
              math={`\\mathrm{NWW}(${x}\\,;\\,${y}) = ${x} \\cdot ${latexExtraB} = ${L}`}
            />
          )}
          {!useAFirst && latexExtraA && (
            <BlockMath
              math={`\\mathrm{NWW}(${x}\\,;\\,${y}) = ${y} \\cdot ${latexExtraA} = ${L}`}
            />
          )}
        </div>
      )}
    </div>
  );
}
